/**
 * Shipping addresses list — synced with zoolyfe.id.
 */
import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
  Alert,
  RefreshControl,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import {
  getShippingAddresses,
  deleteShippingAddress,
  setPrimaryShippingAddress,
} from '../services/shippingAddressApi';
import AppHeader from '../components/AppHeader';
import { goToProfileTab } from '../navigation/goToProfileTab';

export default function ShippingAddressesListScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const returnToReferral = !!route.params?.returnToReferral || !!route.params?.returnParams?.returnToReferral;
  const returnToTukarPoinDetail = !!route.params?.returnToTukarPoinDetail || !!route.params?.returnParams?.returnToTukarPoinDetail;
  const tukarPoinParams = route.params?.tukarPoinParams || route.params?.returnParams?.tukarPoinParams || {};

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Custom Alert Modal State
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
    type: 'error', // 'error' or 'success'
    onConfirm: null
  });

  // Custom Alert Functions
  const showCustomAlert = (title, message, type = 'error', onConfirm = null) => {
    setAlertModal({
      visible: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const hideCustomAlert = () => {
    setAlertModal(prev => ({ ...prev, visible: false }));
  };

  const handleAlertConfirm = () => {
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    hideCustomAlert();
  };

  const load = useCallback(async () => {
    try {
      const rows = await getShippingAddresses();
      setList(rows);
    } catch (e) {
      Alert.alert('Gagal memuat', e.message || 'Coba lagi.');
      setList([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      load();
    }, [load]),
  );

  const onRefresh = () => {
    setRefreshing(true);
    load();
  };

  const goAdd = () => {
    navigation.navigate('ShippingAddress', { returnToReferral, editId: null, returnParams: route.params });
  };

  const goEdit = (item) => {
    navigation.navigate('ShippingAddress', { returnToReferral, editId: item.id, returnParams: route.params });
  };

  const goBack = () => {
    if( returnToTukarPoinDetail ) {
      if( tukarPoinParams ) {
        navigation.navigate('TukarPoinDetail', tukarPoinParams);
      } else {
        goToProfileTab(navigation);
      }
    } else {
      goToProfileTab(navigation);
    }
  }

  const onSetPrimary = async (item) => {
    if (item.alamat_utama === 1) return;
    setBusyId(item.id);
    try {
      await setPrimaryShippingAddress(item.id);
      await load();
    } catch (e) {
      Alert.alert('Gagal', e.message || 'Coba lagi.');
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = (item) => {
    showCustomAlert(
      `Konfirmasi`,
      `Apakah Anda yakin akan menghapus alamat dengan label: "${item.nama_alamat}"?`,
      `error`,
      () => {
        executeDelete(item)
      }
    );
  };

  const executeDelete = async (item) => {
    setBusyId(item.id);
    try {
      await deleteShippingAddress(item.id);
      await load();
    } catch (e) {
      showCustomAlert(
        `Pemberitahuan`,
        e.message || `Terjadi kesalahan saat menghapus alamat. Silakan coba beberapa saat lagi.`,
        `error`,
      );
    } finally {
      setBusyId(null);
    }
  }

  const renderItem = ({ item }) => {
    const line = [item.detail_alamat, item.kecamatan, item.kota, item.provinsi, item.kodepos]
      .filter(Boolean)
      .join(', ');
    const busy = busyId === item.id;
    const isPrimary = item.alamat_utama === 1;
    const showDeleteOthers = list.length > 1 && !isPrimary;

    return (
      <View style={styles.card}>
        <View style={styles.cardTop}>
          <Text style={styles.cardTitle}>{item.nama_alamat || 'Alamat'}</Text>
          {item.alamat_utama === 1 ? (
            <View style={styles.badgeUtama}>
              <Text style={styles.badgeUtamaText}>Utama</Text>
            </View>
          ) : null}
        </View>
        <Text style={styles.cardSub}>{item.nama_penerima} · {item.no_telp}</Text>
        <Text style={styles.cardAddr} numberOfLines={3}>{line}</Text>

        <View style={styles.cardActions}>
          {isPrimary ? (
            <View style={{ flex: 1 }} />
          ) : (
            <>
              <TouchableOpacity
                style={styles.btnSecondary}
                onPress={() => onSetPrimary(item)}
                disabled={busy}
              >
                {busy ? <ActivityIndicator size="small" color="#1E293B" /> : (
                  <Text style={styles.btnSecondaryText}>Jadikan utama</Text>
                )}
              </TouchableOpacity>
              <View style={{ flex: 1 }} />
            </>
          )}
          <TouchableOpacity style={styles.btnSecondary} onPress={() => goEdit(item)} disabled={busy}>
            <Ionicons name="create-outline" size={18} color="#1E293B" />
            <Text style={styles.btnSecondaryText}>Ubah</Text>
          </TouchableOpacity>
          {showDeleteOthers ? (
            <TouchableOpacity style={styles.btnDanger} onPress={() => onDelete(item)} disabled={busy}>
              <Ionicons name="trash-outline" size={18} color="#DC2626" />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safe}>
        <AppHeader title="Alamat pengiriman" onBack={() => goBack()} />
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#64748B" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <AppHeader
        title="Alamat pengiriman"
        onBack={() => goBack()}
        right={(
          <TouchableOpacity onPress={goAdd} hitSlop={12}>
            <Ionicons name="add-circle-outline" size={24} color="#1E293B" />
          </TouchableOpacity>
        )}
      />

      {returnToReferral ? (
        <Text style={styles.topHint}>
          Setelah menambah atau mengubah alamat, kembali ke Kode Referral dan tarik untuk refresh sebelum klaim.
        </Text>
      ) : null}

      <FlatList
        data={list}
        keyExtractor={(it) => String(it.id)}
        renderItem={renderItem}
        contentContainerStyle={list.length === 0 ? styles.emptyContainer : styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ListEmptyComponent={
          <View style={styles.emptyInner}>
            <Ionicons name="location-outline" size={48} color="#CBD5E1" />
            <Text style={styles.emptyTitle}>Belum ada alamat</Text>
            <Text style={styles.emptySub}>Tambah alamat untuk pengiriman hadiah dan pesanan.</Text>
            <TouchableOpacity style={styles.emptyBtn} onPress={goAdd}>
              <Text style={styles.emptyBtnText}>Tambah alamat</Text>
            </TouchableOpacity>
          </View>
        }
      />

      <Modal
        animationType="fade"
        transparent={true}
        visible={alertModal.visible}
        onRequestClose={hideCustomAlert}
      >
        <View style={styles.customModalOverlay}>
          <View style={styles.customModalContainer}>
            <View style={[styles.customModalHeader, alertModal.type === 'success' ? styles.successHeader : styles.errorHeader]}>
              <Text style={styles.customModalTitle}>{alertModal.title}</Text>
            </View>
            <View style={styles.customModalBody}>
              <Text style={styles.customModalMessage}>{alertModal.message}</Text>
            </View>
            <View style={styles.customModalFooter}>
              {alertModal.onConfirm ? (
                <View style={styles.customModalButtonsRow}>
                  <TouchableOpacity 
                    style={[styles.customModalButton, styles.cancelButton]}
                    onPress={hideCustomAlert}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={[styles.customModalButton, alertModal.type === 'success' ? styles.successButton : styles.errorButton]}
                    onPress={handleAlertConfirm}
                  >
                    <Text style={styles.customModalButtonText}>{alertModal.type === 'success' ? 'OK' : 'Confirm'}</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity 
                  style={[styles.customModalButton, alertModal.type === 'success' ? styles.successButton : styles.errorButton]}
                  onPress={hideCustomAlert}
                >
                  <Text style={styles.customModalButtonText}>OK</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#F8FAFC' },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  topHint: {
    marginHorizontal: 16,
    marginTop: 10,
    marginBottom: 4,
    fontSize: 13,
    color: '#64748B',
    lineHeight: 18,
  },
  listContent: { padding: 16, paddingBottom: 32 },
  emptyContainer: { flexGrow: 1 },
  emptyInner: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    marginTop: 40,
  },
  emptyTitle: { fontSize: 17, fontWeight: '700', color: '#334155', marginTop: 12 },
  emptySub: { fontSize: 14, color: '#64748B', textAlign: 'center', marginTop: 8, lineHeight: 20 },
  emptyBtn: {
    marginTop: 20,
    backgroundColor: '#1E293B',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
  },
  emptyBtnText: { color: '#fff', fontWeight: '700', fontSize: 15 },
  card: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  cardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  cardTitle: { fontSize: 16, fontWeight: '700', color: '#0F172A', flex: 1 },
  badgeUtama: {
    backgroundColor: '#DCFCE7',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    marginLeft: 8,
  },
  badgeUtamaText: { fontSize: 11, fontWeight: '700', color: '#166534' },
  cardSub: { fontSize: 13, color: '#64748B', marginBottom: 6 },
  cardAddr: { fontSize: 14, color: '#475569', lineHeight: 20 },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    gap: 8,
  },
  btnSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
  },
  btnSecondaryText: { fontSize: 13, fontWeight: '600', color: '#1E293B' },
  btnDanger: {
    padding: 8,
    marginLeft: 4,
  },

  customModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  customModalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 320,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  customModalHeader: {
    padding: 20,
    paddingBottom: 15,
  },
  successHeader: {
    backgroundColor: '#10B981',
  },
  errorHeader: {
    backgroundColor: '#EF4444',
  },
  customModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  customModalBody: {
    padding: 20,
    paddingTop: 10,
    paddingBottom: 15,
  },
  customModalMessage: {
    fontSize: 16,
    color: '#374151',
    textAlign: 'center',
    lineHeight: 24,
  },
  customModalFooter: {
    padding: 20,
    paddingTop: 10,
  },
  customModalButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  customModalButtonsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  successButton: {
    backgroundColor: '#10B981',
    flex: 1,
  },
  errorButton: {
    backgroundColor: '#EF4444',
    flex: 1,
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    flex: 1,
  },
  customModalButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButtonText: {
    color: '#6B7280',
    fontSize: 16,
    fontWeight: '600',
  },
});
